/**
 * Simplified collaboration provider that handles WebSocket connection
 * to Cloudflare Durable Objects for real-time collaboration.
 * 
 * Uses simple JSON message passing for reliability on free tier.
 */

import { useAuthStore } from './store';

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'offline';

export interface CollaborationUser {
  id: string;
  name: string;
  color: string;
}

interface Message {
  type: 'sync' | 'update' | 'awareness' | 'users' | 'saved' | 'error' | 'ping';
  data?: any;
}

type StatusChangeCallback = (status: ConnectionStatus) => void;
type UsersChangeCallback = (users: CollaborationUser[]) => void;
type ContentChangeCallback = (content: string) => void;
type SaveCallback = (timestamp: number) => void;

export class CollaborationProvider {
  private documentId: string;
  private ws: WebSocket | null = null;
  private status: ConnectionStatus = 'connecting';
  private users: CollaborationUser[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;

  // Callbacks
  private statusChangeCallbacks: StatusChangeCallback[] = [];
  private usersChangeCallbacks: UsersChangeCallback[] = [];
  private contentChangeCallbacks: ContentChangeCallback[] = [];
  private saveCallbacks: SaveCallback[] = [];

  constructor(documentId: string) {
    this.documentId = documentId;
    this.connect();
  }

  private getWebSocketUrl(): string {
    const user = useAuthStore.getState().user;
    
    const apiUrl = import.meta.env.VITE_API_URL;
    let wsUrl: string;
    
    if (apiUrl) {
      wsUrl = apiUrl.replace(/^http/, 'ws');
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}`;
    }
    
    const params = new URLSearchParams({
      docId: this.documentId,
      userId: user?.id || crypto.randomUUID(),
      userName: user?.name || 'Anonymous',
    });

    return `${wsUrl}/ws/${this.documentId}?${params.toString()}`;
  }

  private connect() {
    if (this.destroyed) return;
    
    this.setStatus('connecting');
    
    try {
      const wsUrl = this.getWebSocketUrl();
      console.log('[Collab] Connecting to:', wsUrl);
      
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[Collab] Connected successfully');
        this.reconnectAttempts = 0;
        this.setStatus('connected');
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };

      this.ws.onclose = (event) => {
        console.log('[Collab] Connection closed:', event.code, event.reason);
        this.stopHeartbeat();
        this.handleDisconnect();
      };

      this.ws.onerror = (error) => {
        console.error('[Collab] WebSocket error:', error);
      };
    } catch (error) {
      console.error('[Collab] Failed to connect:', error);
      this.handleDisconnect();
    }
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      }
    }, 30000);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private handleDisconnect() {
    if (this.destroyed) return;
    
    this.setStatus('disconnected');
    this.stopHeartbeat();

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      this.reconnectAttempts++;
      
      console.log(`[Collab] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      this.reconnectTimeout = setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      this.setStatus('offline');
    }
  }

  private handleMessage(data: string | ArrayBuffer) {
    try {
      if (typeof data !== 'string') {
        // Binary message - ignore for now in simplified version
        return;
      }

      const message: Message = JSON.parse(data);

      switch (message.type) {
        case 'sync':
          if (message.data?.content) {
            this.notifyContentChange(message.data.content);
          }
          break;

        case 'update':
          if (message.data?.content) {
            this.notifyContentChange(message.data.content);
          }
          break;

        case 'users':
          if (message.data?.users) {
            this.users = message.data.users;
            this.notifyUsersChange();
          }
          break;

        case 'saved':
          if (message.data?.timestamp) {
            this.saveCallbacks.forEach(cb => cb(message.data.timestamp));
          }
          break;

        case 'error':
          console.error('[Collab] Server error:', message.data);
          break;
      }
    } catch (e) {
      console.error('[Collab] Error parsing message:', e);
    }
  }

  private setStatus(status: ConnectionStatus) {
    this.status = status;
    this.statusChangeCallbacks.forEach(cb => cb(status));
  }

  private notifyUsersChange() {
    this.usersChangeCallbacks.forEach(cb => cb(this.users));
  }

  private notifyContentChange(content: string) {
    this.contentChangeCallbacks.forEach(cb => cb(content));
  }

  // Public API
  public getStatus(): ConnectionStatus {
    return this.status;
  }

  public getUsers(): CollaborationUser[] {
    return this.users;
  }

  public onStatusChange(callback: StatusChangeCallback): () => void {
    this.statusChangeCallbacks.push(callback);
    return () => {
      this.statusChangeCallbacks = this.statusChangeCallbacks.filter(cb => cb !== callback);
    };
  }

  public onUsersChange(callback: UsersChangeCallback): () => void {
    this.usersChangeCallbacks.push(callback);
    return () => {
      this.usersChangeCallbacks = this.usersChangeCallbacks.filter(cb => cb !== callback);
    };
  }

  public onContentChange(callback: ContentChangeCallback): () => void {
    this.contentChangeCallbacks.push(callback);
    return () => {
      this.contentChangeCallbacks = this.contentChangeCallbacks.filter(cb => cb !== callback);
    };
  }

  public onSave(callback: SaveCallback): () => void {
    this.saveCallbacks.push(callback);
    return () => {
      this.saveCallbacks = this.saveCallbacks.filter(cb => cb !== callback);
    };
  }

  public sendUpdate(content: string) {
    this.send({ type: 'update', data: { content } });
  }

  public requestSync() {
    this.send({ type: 'sync' });
  }

  private send(message: Message) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  public reconnect() {
    if (this.destroyed) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close(1000, 'manual reconnect');
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.reconnectAttempts = 0;
    this.connect();
  }

  public destroy() {
    this.destroyed = true;
    this.stopHeartbeat();
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Provider destroyed');
      this.ws = null;
    }

    this.statusChangeCallbacks = [];
    this.usersChangeCallbacks = [];
    this.contentChangeCallbacks = [];
    this.saveCallbacks = [];
  }
}

// Factory function for creating collaboration providers
export function createCollaborationProvider(documentId: string): CollaborationProvider {
  return new CollaborationProvider(documentId);
}
